import { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Box,
  TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from 'react-router-dom';
import { DarkUpperSection, LoadingSpinner, PageSection } from '../../components';
import { useAlert, useCurrentUser } from '../../hooks';
import { listWarehouseEmailConfigRequest, WarehouseEmailConfig } from '../../api/warehouseConfig';
import { captureError } from '../../common/sentry';

export default function WarehouseEmailConfigPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const alert = useAlert();
  const user = useCurrentUser();
  const [warehouses, setWarehouses] = useState<WarehouseEmailConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortAsc, setSortAsc] = useState(true);
  const [search, setSearch] = useState('');

  const filteredWarehouses = warehouses.filter((w) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      w.warehouseId.toLowerCase().includes(term) ||
      w.warehouseLabel.toLowerCase().includes(term) ||
      w.emails.some((e) => e.toLowerCase().includes(term)) ||
      w.coordinatorEmails.some((e) => e.toLowerCase().includes(term))
    );
  });

  const sortedWarehouses = [...filteredWarehouses].sort((a, b) =>
    sortAsc ? Number(a.warehouseId) - Number(b.warehouseId) : Number(b.warehouseId) - Number(a.warehouseId)
  );

  useEffect(() => {
    async function fetchData() {
      try {
        const data = await listWarehouseEmailConfigRequest();
        setWarehouses(data);
      } catch (error) {
        captureError(error, 'warehouseEmailConfig-fetch');
        alert('Unable to load warehouse email configuration', 'error');
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [alert]);

  if (!user.isAdmin) return null;

  return (
    <>
      <DarkUpperSection>
        <Stack spacing={2} sx={{ padding: '1em 0' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton onClick={() => navigate('/settings')} sx={{ color: theme.palette.primary.contrastText }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h2" fontWeight="bold" color={theme.palette.primary.contrastText}>
              Warehouse Email Configurations
            </Typography>
          </Stack>
          <Typography variant="body1" color={theme.palette.primary.contrastText}>
            View the currently configured warehouses, their email distribution lists (for sending QM emails), and
            warehouse coordinator emails
          </Typography>
        </Stack>
      </DarkUpperSection>
      <PageSection>
        <Box
          sx={{
            borderRadius: 2,
            padding: 2,
            border: `1px solid ${theme.palette.primary.light}`,
            backgroundColor: 'rgba(33, 150, 243, 0.04)'
          }}>
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              <strong style={{ color: theme.palette.primary.light }}>Distribution Emails</strong> — recipients of QM
              notification emails (e.g. QM raised, QM closed) for the warehouse.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <strong style={{ color: theme.palette.primary.light }}>Warehouse Coordinator Emails</strong> — users who
              have write access to system-generated QM records for the warehouse, even without full edit permissions.
              Cycle Count QMs and Other Logged Issue QMs fall under system-generated QMs.
            </Typography>
          </Stack>
        </Box>
        <LoadingSpinner isLoading={isLoading}>
          <Stack spacing={2} sx={{ padding: '1.5em 0' }}>
            <TextField
              size="small"
              placeholder="Search by warehouse name, ID, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ maxWidth: 400 }}
            />
            <Typography variant="body2" color="text.secondary">
              {sortedWarehouses.length} of {warehouses.length} warehouse{warehouses.length !== 1 ? 's' : ''} shown
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 700, cursor: 'pointer' }} onClick={() => setSortAsc(!sortAsc)}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <span>Warehouse ID</span>
                        <Stack spacing={-1} sx={{ lineHeight: 0 }}>
                          <ArrowDropUpIcon fontSize="small" sx={{ opacity: sortAsc ? 1 : 0.3 }} />
                          <ArrowDropDownIcon fontSize="small" sx={{ opacity: sortAsc ? 0.3 : 1 }} />
                        </Stack>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Distribution Emails</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Warehouse Coordinator Emails</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedWarehouses.map((warehouse) => (
                    <TableRow key={warehouse.warehouseId}>
                      <TableCell>
                        <Typography variant="body2">{warehouse.warehouseId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {warehouse.warehouseLabel}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {warehouse.emails.map((email) => (
                            <Chip key={email} label={email} size="small" variant="outlined" />
                          ))}
                          {warehouse.emails.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              None configured
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {warehouse.coordinatorEmails.map((email) => (
                            <Chip key={email} label={email} size="small" variant="outlined" color="primary" />
                          ))}
                          {warehouse.coordinatorEmails.length === 0 && (
                            <Typography variant="caption" color="text.secondary">
                              None configured
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sortedWarehouses.length === 0 && warehouses.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No results match your search
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {warehouses.length === 0 && !isLoading && (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          No warehouse configurations found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </LoadingSpinner>
      </PageSection>
    </>
  );
}
